/**
 * c-animate.js — Scroll-entrance animation manager for SPA navigations.
 *
 * Manages product-card entrance animations across SPA navigations:
 *
 *  _animatedEls (Set of card href keys): tracks which cards have already
 *  completed their entrance. These are NEVER reset or re-observed, regardless
 *  of how many times spa:navigated fires or how many navigations happen.
 *
 *  spa:before-swap — suppress #ajaxContainer.preloading container animation
 *  spa:after-swap  — SYNCHRONOUSLY snap above-fold cards to final state before
 *                    any paint frame; apply sold-out opacity in the same tick
 *  spa:navigated   — set up IntersectionObserver for below-fold cards only
 *
 * Above-fold cards: snapped immediately with no transition, sold-out cards
 * correctly at opacity 0.35, normal cards at CSS-default (opacity: 1).
 *
 * Below-fold cards: hidden at opacity 0 / translateY(10px), animated in via
 * IntersectionObserver as they scroll into view. Sold-out cards animate to
 * opacity 0.35 (not 1) to prevent the full-opacity flash before CSS takes over.
 */

/** Stable keys (primary link href) for cards that have completed their entrance. */
const _animatedEls = new Set();

/** Returns a stable string key for a product-card, or null if not determinable. */
function _cardKey(card) {
  return card.querySelector('a[href]')?.href ?? null;
}

/** Returns true when this card should render at reduced opacity (sold-out + fade on). */
function _isSoldOut(card) {
  return card.getAttribute('in-stock') === 'false' && card.hasAttribute('fade');
}

// ---------------------------------------------------------------------------
// Suppress #ajaxContainer.preloading on every SPA swap — we own per-card
// animation now, not the container-level CSS transition.
// ---------------------------------------------------------------------------

document.addEventListener('spa:before-swap', () => {
  if (!document.getElementById('c-animate-suppress')) {
    const style = document.createElement('style');
    style.id = 'c-animate-suppress';
    style.textContent =
      '#ajaxContainer, #ajaxContainer.preloading {' +
      ' transition: none !important; opacity: 1 !important; transform: none !important; }';
    document.head.appendChild(style);
  }
});

// ---------------------------------------------------------------------------
// spa:after-swap — runs synchronously inside performSwap(), before any paint.
// Snaps above-fold cards (and already-animated cards) to their final visible
// state here so they are NEVER in the wrong opacity/transform when first rendered.
// ---------------------------------------------------------------------------

document.addEventListener('spa:after-swap', () => {
  document.getElementById('c-animate-suppress')?.remove();

  // Snap the container itself to visible state.
  const container = document.getElementById('ajaxContainer');
  if (container) {
    container.style.transition = 'none';
    container.style.opacity = '1';
    container.style.transform = 'none';
    requestAnimationFrame(() => {
      container.style.transition = '';
      container.style.opacity = '';
      container.style.transform = '';
    });
  } else {
    return;
  }

  const vwH = window.innerHeight;

  container.querySelectorAll('product-card').forEach((card) => {
    const key = _cardKey(card);
    const rect = card.getBoundingClientRect();
    const aboveFold = rect.bottom > 0 && rect.top < vwH;
    const alreadyAnimated = key && _animatedEls.has(key);

    if (!aboveFold && !alreadyAnimated) return; // Below-fold new card — handled in spa:navigated

    // Snap to final opacity synchronously — no transition, no transform.
    card.style.transition = 'none';
    // Sold-out cards must land at 0.35, not at CSS default (which flash at 1
    // for one frame before the attribute-selector rule is applied).
    card.style.opacity = _isSoldOut(card) ? '0.35' : '';

    if (aboveFold && key) _animatedEls.add(key);
  });
});

// ---------------------------------------------------------------------------
// spa:navigated — set up below-fold card IntersectionObservers.
// Above-fold cards are already handled in spa:after-swap above.
// ---------------------------------------------------------------------------

document.addEventListener('spa:navigated', () => {
  const container = document.getElementById('ajaxContainer');
  if (!container) return;

  const vwH = window.innerHeight;

  container.querySelectorAll('product-card').forEach((card) => {
    const key = _cardKey(card);

    // Skip if already animated or already snapped above-fold.
    if (key && _animatedEls.has(key)) return;

    const rect = card.getBoundingClientRect();
    const aboveFold = rect.bottom > 0 && rect.top < vwH;
    if (aboveFold) return; // Already handled in spa:after-swap

    // Below fold — enter hidden state, fade in on scroll (no transform).
    card.style.transition = 'none';
    card.style.opacity = '0';

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        const el = entry.target;
        const elKey = _cardKey(el);

        // Flush layout so the browser captures the opacity:0 state before
        // applying the transition — prevents a no-op (zero-duration) animation.
        void el.offsetHeight;

        // Sold-out cards animate TO 0.35, not 1, so there is no flash at full
        // opacity when inline styles are cleared after the transition ends.
        el.style.transition = 'opacity 260ms ease-out';
        el.style.opacity = _isSoldOut(el) ? '0.35' : '1';

        if (elKey) _animatedEls.add(elKey);

        // Return element to pure CSS-driven styling once the transition completes.
        el.addEventListener('transitionend', () => {
          el.style.opacity = '';
          el.style.transition = '';
        }, { once: true });
      });
    }, { threshold: 0.05 });

    observer.observe(card);
  });
});
